import { useEffect, useMemo, useState } from 'react';
import {
  addMonths,
  format,
  isSameDay,
  isSameMonth,
  parseISO,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  isToday,
} from 'date-fns';
import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useJobStore } from '../lib/store';
import StatusBadge from '../components/StatusBadge';
import EmptyState from '../components/EmptyState';

export default function Calendar() {
  const { jobs, fetchJobs } = useJobStore();
  const [cursor, setCursor] = useState<Date>(new Date());
  const [selected, setSelected] = useState<Date>(new Date());

  useEffect(() => {
    fetchJobs('All', 'All');
  }, [fetchJobs]);

  const days = useMemo(() => {
    const monthStart = startOfMonth(cursor);
    const monthEnd = endOfMonth(cursor);
    const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
    const arr: Date[] = [];
    let d = gridStart;
    while (d <= gridEnd) {
      arr.push(d);
      d = addDays(d, 1);
    }
    return arr;
  }, [cursor]);

  const jobsByDate = useMemo(() => {
    const map: Record<string, typeof jobs> = {};
    for (const j of jobs) {
      const k = j.date;
      if (!k) continue;
      if (!map[k]) map[k] = [];
      map[k].push(j);
    }
    return map;
  }, [jobs]);

  const selectedKey = format(selected, 'yyyy-MM-dd');
  const selectedJobs = jobsByDate[selectedKey] || [];

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Calendar</h1>
          <p className="text-slate-500 mt-1">See jobs scheduled by date.</p>
        </div>
        <button
          onClick={() => {
            setCursor(new Date());
            setSelected(new Date());
          }}
          className="btn btn-secondary"
        >
          <CalendarDays className="h-4 w-4" /> Today
        </button>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Calendar */}
        <div className="lg:col-span-2 card p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-slate-900">{format(cursor, 'MMMM yyyy')}</h2>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setCursor(addMonths(cursor, -1))}
                className="p-2 rounded-lg hover:bg-slate-100"
                aria-label="Previous month"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                onClick={() => setCursor(addMonths(cursor, 1))}
                className="p-2 rounded-lg hover:bg-slate-100"
                aria-label="Next month"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2">
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
              <div key={d} className="py-1">
                {d}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {days.map((d) => {
              const k = format(d, 'yyyy-MM-dd');
              const has = jobsByDate[k]?.length || 0;
              const inMonth = isSameMonth(d, cursor);
              const isSel = isSameDay(d, selected);
              const today = isToday(d);
              return (
                <button
                  key={k}
                  onClick={() => setSelected(d)}
                  className={`aspect-square rounded-lg text-sm font-medium relative transition ${
                    isSel
                      ? 'bg-brand-600 text-white shadow-sm'
                      : today
                      ? 'bg-brand-50 text-brand-700 font-bold'
                      : inMonth
                      ? 'bg-white hover:bg-slate-100 text-slate-700'
                      : 'text-slate-300 hover:bg-slate-50'
                  }`}
                >
                  <span>{d.getDate()}</span>
                  {has > 0 && (
                    <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-0.5">
                      {Array.from({ length: Math.min(has, 3) }).map((_, i) => (
                        <span
                          key={i}
                          className={`w-1 h-1 rounded-full ${
                            isSel ? 'bg-white' : 'bg-brand-600'
                          }`}
                        />
                      ))}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Day list */}
        <div className="card p-5 sm:p-6">
          <h2 className="text-lg font-bold text-slate-900 mb-1">
            {format(selected, 'EEEE')}
          </h2>
          <p className="text-sm text-slate-500 mb-4">{format(selected, 'd MMMM yyyy')}</p>

          {selectedJobs.length === 0 ? (
            <EmptyState
              icon={CalendarDays}
              title="No jobs"
              subtitle="Nothing booked for this day."
            />
          ) : (
            <div className="space-y-2">
              {selectedJobs
                .sort((a, b) => (a.time || '').localeCompare(b.time || ''))
                .map((job) => (
                  <Link
                    key={job.id}
                    to={`/jobs/${job.id}`}
                    className="block rounded-lg border border-slate-200 p-3 hover:border-brand-300 hover:bg-brand-50/40 transition"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      {job.time && (
                        <span className="text-xs font-bold text-slate-500">{job.time}</span>
                      )}
                      <StatusBadge status={job.status} size="sm" />
                    </div>
                    <div className="font-semibold text-slate-900 text-sm truncate">
                      {job.title}
                    </div>
                    {job.client_name && (
                      <div className="text-xs text-slate-500 truncate mt-0.5">
                        {job.client_name}
                      </div>
                    )}
                  </Link>
                ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
